import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Progress,
  Descriptions,
  Table,
  Typography,
  Alert,
  Spin,
  Tooltip,
} from 'antd';
import { ReloadOutlined, PlusCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  getEsimBundleStatus,
  getEsimTopUps,
  type EsimBundleStatus,
  type LiveBundleBalance,
  type LocalBundle,
  type ProviderTopUp,
} from '../../services/adminService';
import {
  balanceTypeSummary,
  formatMb,
  remainingPercent,
  timeAgo,
  formatValidity,
} from '../../esim/bundleHelpers';
import TopUpEsimModal from './TopUpEsimModal';
import CoverageList from './CoverageList';
import EsimGuideLink from './EsimGuideLink';

const { Text } = Typography;

interface EsimBundleCardProps {
  iccid: string;
  canOperate: boolean;
}

const statusTag = (b: {
  isDepleted?: boolean | null;
  isExpired?: boolean | null;
  status?: string;
}) => {
  if (b.isExpired) return <Tag color="default">Expired</Tag>;
  if (b.isDepleted) return <Tag color="red">Used up</Tag>;
  if (b.status === 'superseded') return <Tag>Replaced</Tag>;
  if (b.status === 'legacy') return <Tag color="purple">Legacy</Tag>;
  return <Tag color="green">Active</Tag>;
};

// Match a live provider entry (no bundle id) to our rows by roaming profile so
// we can show the bundle name and the original allowance next to the balance.
function matchLocal(live: LiveBundleBalance, local: LocalBundle[]): LocalBundle | undefined {
  return (
    local.find(
      l =>
        l.status !== 'superseded' &&
        l.roaming &&
        l.roaming.group === live.roamingGroup &&
        l.roaming.id === live.roamingId
    ) || local.find(l => l.status !== 'superseded')
  );
}

// KAN-167: the "Bundle" card on a Travel eSIM page — live remaining data per
// stacked bundle, what happens when it runs out, expiry, plus top-up and the
// history of everything ever added to the eSIM.
const EsimBundleCard: React.FC<EsimBundleCardProps> = ({ iccid, canOperate }) => {
  const [status, setStatus] = useState<EsimBundleStatus | null>(null);
  const [history, setHistory] = useState<ProviderTopUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const load = useCallback(
    async (refresh: boolean, signal?: AbortSignal) => {
      try {
        const [s, h] = await Promise.all([
          getEsimBundleStatus(iccid, refresh, signal),
          getEsimTopUps(iccid, signal).catch(() => null),
        ]);
        setStatus(s);
        setHistory(h?.provider || []);
        setError(null);
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load the bundle');
      }
    },
    [iccid]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    load(false, controller.signal).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load(true);
    setRefreshing(false);
  };

  const live = status?.live;
  const local = status?.bundles || [];
  const activeLocal = local.filter(l => l.status !== 'superseded');
  const firstRoaming = activeLocal.find(l => l.roaming && l.roaming.id != null)?.roaming;
  const currentRoaming =
    firstRoaming && firstRoaming.id != null
      ? { group: firstRoaming.group, id: firstRoaming.id }
      : null;

  const historyRows = [
    ...history.map((h, i) => ({
      key: `p${i}`,
      when: h.executedAt,
      what: h.bundleId || '—',
      data: h.data || (h.valueMb != null ? formatMb(h.valueMb) : '—'),
      validity: h.validityDays != null ? formatValidity(h.validityDays) : '—',
      type: h.requestType === 'initial' ? 'Initial bundle' : h.requestType || 'Top-up',
      source: 'Vodafone',
    })),
    ...local.map(l => ({
      key: `l${l.id}`,
      when: l.startDate,
      what: l.name || l.bundleId || '—',
      data: l.totalMb != null ? formatMb(l.totalMb) : '—',
      validity: l.endDate ? `until ${new Date(l.endDate).toLocaleDateString('en-GB')}` : '—',
      type:
        l.status === 'superseded'
          ? 'Replaced'
          : l.status === 'legacy'
            ? 'Legacy bundle'
            : 'Portal record',
      source: 'Portal',
    })),
  ].sort((a, b) => (b.when || '').localeCompare(a.when || ''));

  return (
    <Card
      title={
        <Space>
          Bundle & remaining data
          <Tooltip title="Remaining data comes live from Vodafone. Vodafone allows one balance query per minute per eSIM, so the refresh button may serve the last known value for up to a minute.">
            <InfoCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      }
      style={{ marginBottom: 24 }}
      extra={
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            checked {timeAgo(status?.checkedAt)}
          </Text>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          {canOperate && (
            <Button
              size="small"
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => setTopUpOpen(true)}
            >
              Top up
            </Button>
          )}
        </Space>
      }
    >
      {loading ? (
        <Spin />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {status?.providerError && (
            <Alert
              type="warning"
              showIcon
              message="Vodafone did not answer the balance query; showing the last known values."
            />
          )}
          {live && live.length === 0 && activeLocal.length === 0 && (
            <Alert
              type="info"
              showIcon
              message="No bundle on this eSIM"
              description={
                canOperate
                  ? 'Vodafone reports no active bundle. Use "Top up" to add one.'
                  : 'Vodafone reports no active bundle. Ask a reseller admin to add one.'
              }
            />
          )}
          {(live && live.length ? live : activeLocal).map((entry, i) => {
            const isLive = 'remainingMb' in entry && 'expiry' in entry;
            const l = isLive
              ? matchLocal(entry as LiveBundleBalance, local)
              : (entry as LocalBundle);
            const remaining = isLive
              ? (entry as LiveBundleBalance).remainingMb
              : (entry as LocalBundle).remainingMb;
            const total = l?.totalMb ?? null;
            const pct = remainingPercent(remaining, total);
            const expiry = isLive
              ? (entry as LiveBundleBalance).expiry
              : (entry as LocalBundle).endDate;
            const bt = entry.balanceType;
            const roamingGroup = isLive
              ? (entry as LiveBundleBalance).roamingGroup
              : (entry as LocalBundle).roaming?.group;
            const roamingId = isLive
              ? (entry as LiveBundleBalance).roamingId
              : (entry as LocalBundle).roaming?.id;
            // The profile name (e.g. "Poland") comes from our mirrored roaming
            // profile table via the matched local row; the live entry only
            // carries group + numeric id.
            const roamingName = l?.roaming?.name ?? null;
            return (
              <Card
                key={i}
                size="small"
                type="inner"
                title={l?.name || l?.bundleId || 'Bundle'}
                extra={statusTag(entry)}
              >
                <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="Remaining">
                    <Text strong>{formatMb(remaining)}</Text>
                    {total != null && <Text type="secondary"> of {formatMb(total)}</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Expires">
                    {expiry ? (
                      new Date(expiry).toLocaleString('en-GB')
                    ) : (
                      <Text type="secondary">after first use + validity</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="When used up">
                    <Tag color={bt.key === 'standard' ? 'blue' : 'orange'}>{bt.label}</Tag>{' '}
                    {balanceTypeSummary(bt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Coverage">
                    {roamingGroup
                      ? roamingName
                        ? `${roamingName} (${roamingGroup} · profile ${roamingId ?? '—'})`
                        : `${roamingGroup} · profile ${roamingId ?? '—'}`
                      : '—'}
                  </Descriptions.Item>
                </Descriptions>
                {pct != null && (
                  <Progress
                    percent={pct}
                    size="small"
                    status={pct === 0 ? 'exception' : 'active'}
                  />
                )}
                {roamingGroup && roamingId != null && (
                  <div style={{ marginTop: 8 }}>
                    <CoverageList group={roamingGroup} id={roamingId} />
                  </div>
                )}
              </Card>
            );
          })}
          {historyRows.length > 0 && (
            <Table
              size="small"
              title={() => 'Bundle history'}
              dataSource={historyRows}
              pagination={historyRows.length > 8 ? { pageSize: 8 } : false}
              columns={[
                {
                  title: 'When',
                  dataIndex: 'when',
                  render: (d: string | null) => (d ? new Date(d).toLocaleString('en-GB') : '—'),
                },
                { title: 'Bundle', dataIndex: 'what' },
                { title: 'Data', dataIndex: 'data' },
                { title: 'Validity', dataIndex: 'validity' },
                { title: 'Type', dataIndex: 'type' },
                { title: 'Source', dataIndex: 'source' },
              ]}
            />
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Not sure what a bundle type or coverage means? Read the{' '}
            <EsimGuideLink>eSIM guide</EsimGuideLink>.
          </Text>
        </Space>
      )}
      {canOperate && (
        <TopUpEsimModal
          iccid={iccid}
          open={topUpOpen}
          onClose={() => setTopUpOpen(false)}
          onToppedUp={() => load(true)}
          currentRoaming={currentRoaming}
        />
      )}
    </Card>
  );
};

export default EsimBundleCard;
